import { Component, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import type { ModalOptions } from '@ionic/angular';
import { ModalController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { timer } from 'rxjs';

// import { ViewBasePage } from '@shared/pages';
import { AddressBasePage, RadioModalPage } from '@shared/pages';
import { AddressVerificationService, ServiceAreasService } from '@shared/services';
import type { TAddress, TServiceArea, TTicketsViewModel } from '@shared/models';

@Component({
    'selector': 'app-ticket2Destination',
    'templateUrl': './ticket2Destination.page.html',
    'styleUrls': ['./ticket2Destination.page.scss'],
    'standalone': true,
    'imports': [ CommonModule, FormsModule, IonicModule ]
})
export class Ticket2DestinationPage extends AddressBasePage implements OnInit, OnDestroy {
    private zipcodePromptSelectedZipCity: string;
    private zipsCities = [];
    public vm: TTicketsViewModel;
    private regionName = '';
    private countryName = '';
    public submitted = false;
    public radioGroupValue: string;

    constructor(private serviceAreasService: ServiceAreasService, 
        private addressVerificationService: AddressVerificationService,
        private modalCtrl: ModalController, injector: Injector) {
        super(injector);
    }

    public ionViewWillEnter(): void {
        super.setCurrentView(this.Constants.VIEW_TICKETS_PAGE2);
    }

    public override ngOnInit(): void {
        super.ngOnInit();

        this.vm = super.getViewModel();
        if (this.vm.ticket.fromDealership) {
            this.onToggleDirectionClick('dropoff');
            this.radioGroupValue = 'dropoff';
        } else {
            this.onToggleDirectionClick('pickup');
            this.radioGroupValue = 'pickup';
        }

        // // If are just now creating this ticket, then require the address to be verified.
        // if (!this.vm.ticket.uid) {
        //     this.vm.ticket.addressVerified = false;
        // }

        // this.dealershipStreetAddress = this.sessionService.loggedIn.accountProfile.fullAddress;
        // this.dealershipcoordinate = this.sessionService.loggedIn.accountProfile.coordinate;

        this.loadServiceAreas();
        const dealership = this.sessionService.getDealership();

        // const dealershipCity    = this.sessionService.getDealership().address.city;
        // const dealershipZipcode = this.sessionService.getDealership().address.zipcode;
        const zipCity = dealership.address.zipcode + ' - ' + dealership.address.city;
        this.zipcodePromptSelectedZipCity = zipCity;

        this.regionName = dealership.address.regionName;
        this.countryName = dealership.address.countryName;
        
        // Initialize addressVerified if it's undefined
        // Check if the address has already been verified (has coordinate)
        if (this.vm.addressVerified === undefined) {
            const hasCoordinate = this.vm.ticket.fromDealership 
                ? !!this.vm.ticket.dropoff?.address?.coordinate 
                : !!this.vm.ticket.pickup?.address?.coordinate;
            this.vm.addressVerified = hasCoordinate;
        }
        
        // If address is verified (either already set or just initialized), mark step as complete
        if (this.vm.addressVerified) {
            this.vm.step2Complete = true;
        }
        // this.initMoreMenu();
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    // public initMoreMenu(): void {
    //     this.resetPopoverMenu();
    //     this.pushPopoverMenuItem('Show Tour', 'show-ticket-2-tour');
    // }

    public async onStreetAddressChange() {
        this.vm.addressVerified = false;
    }

    public async onZipcodeChange() {
        this.vm.addressVerified = false;
        let zipcode = (this.vm.ticket.fromDealership) ? this.vm.ticket.dropoff.address.zipcode : this.vm.ticket.pickup.address.zipcode;
        zipcode = zipcode?.toString() || '';

        if (zipcode.length === 5) {
            try {
                await this.uxHelperService.showSpinner('Loading data.');
                const serviceArea: TServiceArea = await this.serviceAreasService.getByUid(zipcode);
                await this.uxHelperService.hideSpinner();

                if (serviceArea) {
                    this.useZipcodeCity(serviceArea.city, serviceArea.zipcode);
                } else {
                    this.useZipcodeCity('', zipcode);
                    this.uxHelperService.showAlert('Error', `${zipcode}: is not one of our service areas.`);
                }
            } catch (err) {
                await this.uxHelperService.hideSpinner();
                const errorMessage = this.utilsService.extractError(err);
                this.uxHelperService.showAlert('Error', errorMessage);
            }
        }
    }

    public onNextClick(form: NgForm) {
        this.submitted = true;
        if (!form.valid) {
            return;
        }

        if (this.vm.step2Complete) {
            this.sessionService.setViewModel(this.vm);
            this.router.navigateByUrl('/tickets/ticket3Save');
        }
    }

    private async loadServiceAreas() {
        const serviceAreas: any = await this.serviceAreasService.getAll();
        serviceAreas.forEach((area) => {
            const line1 = area.zipcode + ' - ' + area.city;
            const line2 = area.distance + ' miles away';
            this.zipsCities.push(
                {
                    'line1': line1,
                    'line2': line2,
                    'name': line1 + '[' + line2 + ']',
                    'zipcode': area.zipcode,
                    'city': area.city,
                    'distance': area.distance,
                    'selected': false
                }
            );
        });
    }

    public onToggleDirectionClick(direction: string) {
        this.vm.ticket.fromDealership = (direction === 'dropoff');
        this.vm.directionStr = (this.vm.ticket.fromDealership) ? 'From Dealership' : 'To Dealership';
    }

    public async openZipcodePromptClick() {
        const items2 = this.zipsCities.map(item => item.line1);

        const options: ModalOptions = {
            'component': RadioModalPage,
            'componentProps': { 'items': items2, 'selectedItem': this.zipcodePromptSelectedZipCity, 'title': 'Select City and Zipcode' },
            'backdropDismiss': false,
            'showBackdrop': true,
            'cssClass': 'ticketPage2RadioModal'
        };

        const myModal = await this.modalController.create(options);
        await myModal.present();
        const response: any = await myModal.onDidDismiss();
        if (response.data.okay) {
            this.zipcodePromptSelectedZipCity = response.data.selectedItem;
            const arr = this.zipcodePromptSelectedZipCity.split(' - ');
            const zip = arr[0];
            const city = arr[1];
            this.useZipcodeCity(city, zip);
            // We have a new zipcode and city, the address needs to be reverified.
            this.vm.addressVerified = false;
        }
    }

    private useZipcodeCity(city: string, zipcode: string): void {
        if (this.vm.ticket.fromDealership) {
            this.vm.ticket.dropoff.address.zipcode = zipcode;
            this.vm.ticket.dropoff.address.city = city;
            this.vm.addressVerified = false;
        } else {
            this.vm.ticket.pickup.address.zipcode = zipcode;
            this.vm.ticket.pickup.address.city = city;
            this.vm.addressVerified = false;
        }
    }

    public async onVerifyDropoffAddressClick(form: NgForm) {
        this.submitted = true;
        if (!form.valid) {
            return;
        }
        try {
            this.vm.addressVerified = false;
            await this.uxHelperService.showSpinner('Verifing address.');

            const requestedAddress: TAddress = this.vm.ticket.dropoff.address;
            const validation = await this.addressVerificationService.verifyAddress(requestedAddress);

            if (validation.found) {
                this.vm.ticket.dropoff.address.coordinate = validation.location.coordinate;
                this.vm.ticket.dropoff.address.streetAddress = validation.foundAddress;
                this.vm.ticket.dropoff.address.fullAddress = validation.location.formattedAddress;

                // The user may have typed in "3411 st rose pkwy" and we may have found the address
                // "3411 Saint Rose Parkway".  We want to use the correct spelling of the address.
                // So, the code above just set the found address.  Which made the Angular Change
                // Detection fire which made a call to the method onStreetAddressChange() which
                // set the variable this.vm.ticket.addressVerified to false.  We need it to be 
                // true so the Next button gets enabled.  So, delay a while and then set the
                // vm.ticket.addressVerified to true. 
                timer(100).subscribe(() => {
                    this.vm.addressVerified = true;
                    this.vm.addressVerifiedMsg = validation.statusTitle;
                    this.vm.step2Complete = true;
                });
            }
            await this.uxHelperService.hideSpinner();
            await this.potentiallyUseFoundInfo(validation, this.vm.ticket.dropoff.address);
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            this.vm.ticket.dropoff.address.coordinate = null;
            this.vm.addressVerified = false;
            this.vm.addressVerifiedMsg = 'Address Not Found.';
            const errorMessage = this.utilsService.extractError(err, 'Address Not Found');
            this.uxHelperService.showAlert('Address Not Found', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }

    public async onVerifyPickupAddressClick(form: NgForm) {
        this.submitted = true;
        if (!form.valid) {
            return;
        }

        try {
            this.vm.addressVerified = false;
            await this.uxHelperService.showSpinner('Verifing address.');

            const requestedAddress: TAddress = this.vm.ticket.pickup.address;
            const validation = await this.addressVerificationService.verifyAddress(requestedAddress);

            if (validation.found) {
                this.vm.ticket.pickup.address.coordinate = validation.location.coordinate;
                this.vm.ticket.pickup.address.streetAddress = validation.foundAddress;
                this.vm.ticket.pickup.address.fullAddress = validation.location.formattedAddress;

                // The user may have typed in "3411 st rose pkwy" and we may have found the address
                // "3411 Saint Rose Parkway".  We want to use the correct spelling of the address.
                // So, the code above just set the found address.  Which made the Angular Change
                // Detection fire which made a call to the method onStreetAddressChange() which
                // set the variable this.vm.ticket.addressVerified to false.  We need it to be 
                // true so the Next button gets enabled.  So, delay a while and then set the
                // vm.ticket.addressVerified to true. 
                timer(100).subscribe(() => {
                    this.vm.addressVerified = true;
                    this.vm.addressVerifiedMsg = validation.statusTitle;
                    this.vm.step2Complete = true;
                });
            }
            await this.uxHelperService.hideSpinner();
            await this.potentiallyUseFoundInfo(validation, this.vm.ticket.pickup.address);

            // await this.uxHelperService.showAlert(validation.statusTitle, validation.statusMessage, async (response) => {
            //     if (validation.streetNamePartialMatch) {
            //         const title = 'However, a nearby partial match was found. Do you want to use this address?';
            //         this.uxHelperService.showConfirmationAlert(title, validation.foundAddress, async (response) => {
            //             if (response.selection) {
            //                 this.vm.ticket.pickup.streetAddress = validation.foundAddress;
            //             }
            //         });
            //     }
            // });
        } catch (err) {
            this.vm.ticket.pickup.address.coordinate = null;
            this.vm.addressVerified = false;
            this.vm.addressVerifiedMsg = 'Address Not Found.';
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err, 'Address Not Found');
            this.uxHelperService.showAlert('Address Not Found', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }

    public isAddressVerified(): boolean {
        return this.vm.addressVerified;
    }
}
